import axios, {
  type AxiosError,
  type AxiosInstance,
  type InternalAxiosRequestConfig,
} from 'axios'

import { isConfirmedAuthFailure } from './authFailure'

interface RetriableRequestConfig extends InternalAxiosRequestConfig {
  _authRetried?: boolean
  _readRetries?: number
}

interface ApiClientOptions {
  refresh: () => Promise<string>
  onAuthFailure: () => void
  baseURL?: string
  retryDelay?: (attempt: number) => Promise<void>
}

export interface ApiClientRuntime {
  client: AxiosInstance
  setAccessToken: (token: string | null) => void
  getAccessToken: () => string | null
}

export function createApiClient(options: ApiClientOptions): ApiClientRuntime {
  let accessToken: string | null = null
  let refreshPromise: Promise<string> | null = null
  const client = axios.create({
    baseURL: options.baseURL ?? '/app/api/v1',
    withCredentials: true,
    timeout: 20_000,
  })
  const retryDelay = options.retryDelay ?? ((attempt: number) => new Promise((resolve) => {
    window.setTimeout(resolve, attempt === 1 ? 250 : 750)
  }))

  const setAccessToken = (token: string | null) => {
    accessToken = token
  }

  client.interceptors.request.use((config) => {
    if (accessToken) config.headers.Authorization = `Bearer ${accessToken}`
    return config
  })

  client.interceptors.response.use(undefined, async (error: AxiosError) => {
    const config = error.config as RetriableRequestConfig | undefined
    const transientReadFailure = config?.method?.toLowerCase() === 'get'
      && !axios.isCancel(error)
      && (!error.response || error.response.status >= 500)
    if (config && transientReadFailure && (config._readRetries ?? 0) < 2) {
      config._readRetries = (config._readRetries ?? 0) + 1
      await retryDelay(config._readRetries)
      return client(config)
    }
    if (error.response?.status !== 401 || !config || config._authRetried) throw error
    config._authRetried = true
    const failedAuthorization = config.headers.Authorization
    if (accessToken && failedAuthorization !== `Bearer ${accessToken}`) {
      config.headers.Authorization = `Bearer ${accessToken}`
      return client(config)
    }

    if (!refreshPromise) {
      refreshPromise = options.refresh()
        .then((token) => {
          setAccessToken(token)
          return token
        })
        .catch((refreshError: unknown) => {
          if (isConfirmedAuthFailure(refreshError)) {
            setAccessToken(null)
            options.onAuthFailure()
          }
          throw refreshError
        })
        .finally(() => { refreshPromise = null })
    }
    const token = await refreshPromise
    config.headers.Authorization = `Bearer ${token}`
    return client(config)
  })

  return { client, setAccessToken, getAccessToken: () => accessToken }
}
